/**
 * termMarks — shared keyword-highlighting helpers for the transparent text
 * overlay used by the page-image viewers (SBA/EPAR/CA/AU via PageRenderer and
 * the FDA-letter viewer via FdaLetterPageViewer).
 *
 * These turn a `highlightTerms: string[]` (+ parallel `highlightColors`) into
 * colored `<mark>` spans rendered over the rasterised page image: the text
 * itself stays transparent (it sits on top of the page bitmap), but the
 * matched word(s) get a translucent per-row background so the keyword is
 * visible on the page exactly where it appears.
 *
 * Lifted verbatim from the original PageRenderer implementation so both the
 * regulatory viewer and the FDA-letter viewer share ONE matcher (no
 * copy-paste divergence).
 */
import React from 'react';

const DEFAULT_MARK_BG = 'rgba(250, 204, 21, 0.42)'; // amber, single/uncolored
const DEFAULT_MARK_RING = 'rgba(202, 138, 4, 0.55)';

/** Source for English Exact literals over raw text. Wildcards retain their
 * existing caller-specific semantics. */
export function exactTermSource(term: string): string | null {
  if (term.includes('*')) return null;
  const words = term.toLowerCase().replace(/[^a-z0-9.]+/g, ' ').split(/\s+/)
    .filter((word) => /[a-z0-9]/.test(word));
  if (!words.length) return null;
  const last = words.length - 1;
  if (/[a-z0-9]\.$/.test(words[last]!)) words[last] = words[last]!.slice(0, -1);
  const body = words.map((word) => word.replace(/\./g, '\\.')).join('[^a-z0-9.]+');
  const dotGuard = /^[0-9.]/.test(words[0]!) ? '(?<!\\.)' : '';
  return `(?<![a-z0-9])(?<![a-z0-9]\\.)${dotGuard}${body}(?![a-z0-9])(?!\\.[a-z0-9])`;
}

/** A per-term matcher: its regex + (optional) row color. */
export type TermMatcher = { re: RegExp; color: string | undefined };

/** Per-term matchers from terms (+ parallel colors). One regex PER term so a
 *  match can be attributed back to its query row → its color. */
export function buildColoredMatchers(
  terms?: string[] | null,
  colors?: (string | undefined)[] | null,
  englishExact = false,
): TermMatcher[] {
  if (!terms || !terms.length) return [];
  const esc = (x: string) => x.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const out: TermMatcher[] = [];
  terms.forEach((term, i) => {
    const tokens = (term ?? '').trim().split(/\s+/).filter(Boolean);
    if (!tokens.length) return;
    const pat = (englishExact && exactTermSource(term)) || tokens.map((t) => t.split('*').map(esc).join('\\w*')).join('\\s+');
    try {
      out.push({ re: new RegExp(pat, 'gi'), color: colors?.[i] });
    } catch { /* skip bad pattern */ }
  });
  return out;
}

/** Hex (#rrggbb) → translucent rgba so the page-image text shows through the
 *  PDF highlight overlay (same readability as the amber default). */
export function translucent(hex: string | undefined, alpha: number): string {
  if (!hex) return alpha > 0.5 ? DEFAULT_MARK_RING : DEFAULT_MARK_BG;
  const h = hex.replace('#', '');
  if (h.length !== 6) return alpha > 0.5 ? DEFAULT_MARK_RING : DEFAULT_MARK_BG;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Wrap matches in <mark> (transparent text; translucent per-row background so it
 *  paints behind the page image). Multi-row keyword queries get each row's color;
 *  single/uncolored stays amber. Overlaps resolve earliest/longest. */
export function withTermMarks(text: string, matchers: TermMatcher[]): React.ReactNode {
  if (!matchers.length) return text;
  const ranges: { start: number; end: number; color: string | undefined }[] = [];
  for (const { re, color } of matchers) {
    re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      if (m[0].length === 0) { re.lastIndex++; continue; }
      ranges.push({ start: m.index, end: m.index + m[0].length, color });
    }
  }
  if (!ranges.length) return text;
  ranges.sort((a, b) => a.start - b.start || (b.end - b.start) - (a.end - a.start));
  const out: React.ReactNode[] = [];
  let pos = 0;
  let k = 0;
  for (const r of ranges) {
    if (r.start < pos) continue; // overlap → keep earliest/longest already emitted
    if (r.start > pos) out.push(text.slice(pos, r.start));
    out.push(
      React.createElement(
        'mark',
        {
          key: `m${k++}`,
          style: {
            background: translucent(r.color, 0.42),
            color: 'transparent',
            borderRadius: '2px',
            padding: 0,
            boxShadow: `0 0 0 1px ${translucent(r.color, 0.7)}`,
          },
        },
        text.slice(r.start, r.end),
      ),
    );
    pos = r.end;
  }
  if (pos < text.length) out.push(text.slice(pos));
  return out;
}
